import {
  AfterContentChecked,
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  OnInit,
  Renderer2
} from '@angular/core';

@Component({
  selector: 'axis-accordion-group-wrapper',
  templateUrl: './accordiongroup.component.html',
  host: { class: 'axis-accordion-group-wrapper' }
})

export class AccordionComponent implements AfterContentInit, OnDestroy, AfterContentChecked {
  public expandAllAccordions: boolean;
  public expandCollapseToggler = false;
  public initExpandCollapseFlag = false;
  @Output() onGroupAccordionChange = new EventEmitter<boolean>();

  @ContentChildren(forwardRef(() => AccordionGroupComponent)) groups: any;
  @Input() closeOthers = false;
  @Input() font = 'custom';

  ngAfterContentInit() {
    if (this.groups.length > 1) {
      this.initExpandCollapseFlag = true;
    } else {
      this.initExpandCollapseFlag = false;
    }
    this.expandCollapseTogglerLogic();
    this.groups.toArray().forEach((group: AccordionGroupComponent, index) => {
      group.onAccordionChange.subscribe(() => {
        if (this.closeOthers === true) {
          if (
            (group.active && !group.isCollapse && this.closeOthers) ||
            (group.active && group.isCollapse && this.expandAllAccordions)
          ) {
            this.groups.forEach((openGroup: AccordionGroupComponent) => {
              if (group !== openGroup) {
                openGroup.active = false;
              }
            });
          }
        }
      });
    });
  }

  ngAfterContentChecked() {
    if (this.initExpandCollapseFlag) {
      return;
    } else if (!this.initExpandCollapseFlag) {
      if (this.groups && this.groups.length > 1) {
        this.initExpandCollapseFlag = true;
      }
    }
  }

  // toggles all accordions
  toggleAll(event: Event): void {
    event.preventDefault();
    this.expandAllAccordions = false;
    this.groups.forEach((group: AccordionGroupComponent) => {
      if (!group.disabled) {
        group.isCollapse = !group.isCollapse;
        if (!group.isCollapse) {
          group.active = true;
        } else {
          group.active = false;
        }
      }
    });
  }

  // expands all accordions
  expandAll(event: Event): void {
    event.preventDefault();
    this.groups.forEach((group: AccordionGroupComponent) => {
      if (!group.disabled) {
        group.isCollapse = false;
        group.active = true;
      }
    });
  }

  // collapse all accordions
  collapseAll(event: Event): void {
    event.preventDefault();
    this.groups.forEach((group: AccordionGroupComponent) => {
      if (!group.disabled) {
        if (!group.isCollapse) {
          group.active = false;
        }
      }
    });
  }

  // logic for expandAll and collapseAll
  expandCollapseTogglerLogic() {
    let counter = 0;
    let enabledItemsCount = 0;
    this.groups.forEach((group: AccordionGroupComponent) => {
      if (group.active) {
        counter++;
      }
      if (!group.disabled) {
        enabledItemsCount++;
      }
    });
    if (counter === enabledItemsCount && counter > 0) {
      this.expandCollapseToggler = true;
    } else {
      this.expandCollapseToggler = false;
    }

    if (counter === 0) {
      this.onGroupAccordionChange.emit(true);
    } else {
      this.onGroupAccordionChange.emit(false);
    }
  }

  toggleAccordionItems(event: Event) {
    event.preventDefault();
    this.closeOthers = false;
    this.groups.forEach((group: AccordionGroupComponent) => {
      if (!group.disabled) {
        if (this.expandCollapseToggler) {
          group.active = true;
        } else {
          group.active = false;
        }
      }
    });
  }

  // opens a respective accordion on clicking an external button
  openAccordionItem(index: number) {
    this.groups.forEach((group: AccordionGroupComponent, idx: number) => {
      if (!group.disabled && idx === index) {
        group.active = true;
      }
    });
  }

  // closes a respective accordion on clicking an external button
  closeAccordionItem(index: number) {
    this.groups.forEach((group: AccordionGroupComponent, idx: number) => {
      if (!group.disabled && idx === index) {
        group.active = false;
      }
    });
  }

  // toggles a respective accordion on clicking an external button
  toggleAccordionItem(index: number) {
    this.groups.forEach((group: AccordionGroupComponent, idx: number) => {
      if (!group.disabled && idx === index) {
        group.active = !group.active;
      }
    });
  }

  ngOnDestroy() {
    if(this.groups) {
      this.groups.toArray().forEach((group: AccordionGroupComponent) => {
        group.onAccordionChange.unsubscribe();
      });
      this.groups = null;
    }
  }
}

@Component({
  selector: 'axis-accordion-wrapper',
  templateUrl: './accordion.component.html',
  host: { class: 'axis-accordion-wrapper' }
})
export class AccordionGroupComponent implements OnInit {

  private _active = false;
  private _isexpandAll = false;
  private _isCollapse = true;

  @Input()
  set active(value: boolean) {
    this._active = value;
    this.onAccordionChange.emit(value);
  }
  get active() {
    return this._active;
  }

  @Input()
  set isCollapse(value: boolean) {
    this._isCollapse = value;
  }

  get isCollapse() {
    return this._isCollapse;
  }

  @Input() disabled: boolean;
  @Input() heading: string;
  @Input() ucid = '';
  @Output() onAccordionChange = new EventEmitter();

  constructor(public accordion: AccordionComponent) { }

  ngOnInit(): void {
    // generate a unique component id
    this.ucid = this.ucid + '-' + (Math.random() + Math.random()).toString().replace('.', '');
  }

  // keyboard accessible accordion selection
  setActiveAccordion(event: any) {
    event.preventDefault();

    let target;
    const targetRoot = event.target.parentNode.parentNode;

    switch (event.key.toLocaleLowerCase()) {
      case 'arrowup':
        target = targetRoot.previousElementSibling;
        if (targetRoot.previousElementSibling !== null) {
          if (targetRoot.previousElementSibling.className !== 'axis-accordion-wrapper') {
            target = null;
          }
        }
        if (target === null) {
          target = targetRoot.parentNode.lastElementChild;
        }
        break;
      case 'arrowdown':
        target = targetRoot.nextElementSibling;
        if (target === null) {
          target = targetRoot.parentElement.querySelectorAll('axis-accordion-wrapper')[0];
        }
        break;
      case 'end':
        target = targetRoot.parentNode.lastElementChild;
        break;
      case 'home':
        target = targetRoot.parentNode.querySelector('axis-accordion-wrapper'); // don't select expand all el
        break;
    }

    target = target.querySelector('.axis-accordion-heading-wrapper');
    target.focus();
  }

  // Toggles the Accordion when clicked
  toggleOpen(): void {
    this._isCollapse = this._active;
    this.active = !this.active;
    this.accordion.expandCollapseTogglerLogic();
  }
}

// Expand Collapse
@Component({
  selector: 'axis-expand-collapse',
  templateUrl: './expand-collapse.html',
  host: { class: 'axis-expand-collapse' }
})
export class AccordionExpandCollapseComponent { }

// Expand Collapse Right
@Component({
  selector: 'axis-expand-collapse-right',
  template: `<button
               *ngIf="accordionComponent.initExpandCollapseFlag"
               [attr.aria-expanded]="accordionComponent.expandCollapseToggler"
               axis-button
               (click)="expandCollapse($event)"
               data-kind="link"
             >
               <ng-container *ngIf="!accordionComponent.expandCollapseToggler">
                 <axis-icon
                   icon="add"
                   [font]="accordionComponent.font"
                 ></axis-icon>
                 {{expandText}}
               </ng-container>
               <ng-container *ngIf="accordionComponent.expandCollapseToggler">
                  <axis-icon
                    icon="remove"
                    [font]="accordionComponent.font"
                  ></axis-icon>
                  {{collapseText}}
               </ng-container>
             </button>
            `,
  host: { class: 'axis-expand-collapse-right' }
})

export class AccordionExpandCollapseRightComponent {
  @Input() collapseText = 'Collapse All';
  @Input() expandText = 'Expand All';
  @Output() onGroupAccordionChange = new EventEmitter<boolean>();
  constructor(public accordionComponent: AccordionComponent) { }

  expandCollapse(event: Event) {
    this.accordionComponent.expandCollapseToggler = !this.accordionComponent
      .expandCollapseToggler;
    this.accordionComponent.toggleAccordionItems(event);
    this.onGroupAccordionChange.emit(!this.accordionComponent.expandCollapseToggler);
  }
}

// Expand Collapse Body
@Component({
  selector: 'axis-expand-collapse-body',
  template: '<ng-content></ng-content>',
  host: { class: 'axis-expand-collapse-body' }
})
export class AccordionExpandCollapseBodyComponent { }

@Component({
  selector: 'axis-accordion-body',
  template: '<ng-content></ng-content>',
  host: { class: 'axis-accordion-body', role: 'region' }
})
export class AccordionBodyComponent implements OnInit {

  constructor(private accordionGroupComponent: AccordionGroupComponent,
    private hostElement: ElementRef,
    private renderer: Renderer2) { }

  ngOnInit() {
    // tslint:disable-next-line: max-line-length
    this.renderer.setAttribute(this.hostElement.nativeElement, 'aria-labelledby', 'axis-accordion-header' + this.accordionGroupComponent.ucid);
    this.renderer.setAttribute(this.hostElement.nativeElement, 'id', 'axis-accordion-child' + this.accordionGroupComponent.ucid);
  }
}

@Component({
  selector: 'axis-accordion-heading',
  template: '<ng-content></ng-content>',
  host: { class: 'axis-accordion-heading' }
})
export class AccordionHeadingComponent { }

@Component({
  selector: 'axis-accordion-heading-right',
  template: '<ng-content></ng-content>',
  host: { class: 'axis-accordion-heading-right' }
})
export class AccordionHeadingRightComponent { }
